import { apiRequest } from '@/lib/api';

export type TeamRole = 'ADMIN' | 'PM' | 'FIELD';
export type TeamStatus = 'ACTIVE' | 'INVITED' | 'INACTIVE' | 'EXPIRED';
export type TeamPermissionKey =
  | 'createClients'
  | 'createProjects'
  | 'createTickets'
  | 'scheduleWork'
  | 'viewPayments'
  | 'recordPayments'
  | 'sendPaymentFollowups'
  | 'exportBillingReports';
export type TeamPermissionMap = Record<TeamPermissionKey, boolean>;

export type TeamMember = {
  id: string;
  kind: 'user' | 'invitation';
  fullName?: string | null;
  email: string;
  role: TeamRole;
  permissions?: TeamPermissionMap;
  status: TeamStatus;
  isActive: boolean;
  emailNotificationsEnabled: boolean;
  canConfigureEmailNotifications: boolean;
  lastLoginAt?: string | null;
  createdAt?: string | null;
  inviteSentAt?: string | null;
  inviteExpiresAt?: string | null;
};

export function fetchTeamMembers() {
  return apiRequest<TeamMember[]>('/users', { method: 'GET' });
}

export function inviteTeamMember(payload: {
  fullName?: string;
  email: string;
  role: TeamRole;
  permissions?: TeamPermissionMap;
  emailNotificationsEnabled?: boolean;
}) {
  return apiRequest<TeamMember>('/users/invite', {
    method: 'POST',
    body: payload,
  });
}

export function resendInvitation(invitationId: string) {
  return apiRequest<{ success: boolean }>(`/users/invitations/${invitationId}/resend`, {
    method: 'POST',
  });
}

export function removeInvitation(invitationId: string) {
  return apiRequest<{ success: boolean }>(`/users/invitations/${invitationId}`, {
    method: 'DELETE',
  });
}

export function updateTeamMemberRole(
  userId: string,
  role: TeamRole,
  permissions?: TeamPermissionMap,
  emailNotificationsEnabled?: boolean,
) {
  return apiRequest<{ id: string; role: TeamRole; permissions: TeamPermissionMap }>(`/users/${userId}/role`, {
    method: 'PATCH',
    body: { role, permissions, emailNotificationsEnabled },
  });
}

export function updateTeamMemberStatus(userId: string, isActive: boolean) {
  return apiRequest<{ id: string; isActive: boolean }>(`/users/${userId}/status`, {
    method: 'PATCH',
    body: { isActive },
  });
}

export function updateTeamMemberEmailNotifications(userId: string, enabled: boolean) {
  return apiRequest<{ id: string; emailNotificationsEnabled: boolean }>(
    `/users/${userId}/email-notifications`,
    {
      method: 'PATCH',
      body: { enabled },
    },
  );
}

export function removeTeamMember(userId: string) {
  return apiRequest<{ success: boolean }>(`/users/${userId}`, {
    method: 'DELETE',
  });
}
